import { useEffect, useMemo, useState } from 'react'
import {
  AccountRole,
  AccountStatus,
  AdminDto,
  AdminPermission,
  AuthenticatedAccountDto,
  UserDto,
} from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import AccountStatusFilter from '../../components/admin/AccountStatusFilter'
import AdminModal from '../../components/modals/AdminModal'
import { adminService } from '../../services/admin.service'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useSearch } from '../../contexts/SearchContext'
import { usePermissions } from '../../contexts/PermissionsContext'

type UsersView = 'users' | 'internal' | 'admins' | 'checked-in' | 'logins'

const VIEW_TABS: Array<{ key: UsersView; label: string; permission: AdminPermission }> = [
  { key: 'users', label: 'Users', permission: AdminPermission.Users },
  { key: 'internal', label: 'Internal', permission: AdminPermission.Users },
  { key: 'admins', label: 'Admins', permission: AdminPermission.Admin },
  { key: 'checked-in', label: 'Checked in', permission: AdminPermission.CheckedInUsers },
  { key: 'logins', label: 'Current logins', permission: AdminPermission.AuthenticatedAccounts },
]

const USERS_LIST_VIEWS: UsersView[] = ['users', 'internal']

const ACCOUNT_STATUS_OPTIONS = Object.values(AccountStatus)

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 220
  const paginationHeight = 60

  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)

  return Math.max(10, Math.min(100, calculatedPageSize))
}

function computeInactiveDays(lastActiveAt?: string | Date | null, createdAt?: string | Date | null): string {
  const reference = lastActiveAt ?? createdAt
  if (!reference) return '-'

  const referenceMs = new Date(reference).getTime()
  if (Number.isNaN(referenceMs)) return '-'

  const diffMs = Date.now() - referenceMs
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  return String(Math.max(0, days))
}

function parseStatusesFromParams(searchParams: URLSearchParams): AccountStatus[] {
  const statusParam = searchParams.get('status')
  if (!statusParam) {
    return [AccountStatus.Active]
  }

  const parsed = statusParam
    .split(',')
    .map(part => part.trim().toLowerCase())
    .filter(part => part.length > 0)
    .map(part => ACCOUNT_STATUS_OPTIONS.find(status => status.toLowerCase() === part))
    .filter((status): status is AccountStatus => status !== undefined)

  return parsed.length > 0 ? parsed : [AccountStatus.Active]
}

function statusesToParam(statuses: AccountStatus[]): string {
  return statuses.join(',')
}

const UsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { searchValue } = useSearch()
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions()

  const [users, setUsers] = useState<UserDto[]>([])
  const [admins, setAdmins] = useState<AdminDto[]>([])
  const [checkedInUsers, setCheckedInUsers] = useState<UserDto[]>([])
  const [authenticatedAccounts, setAuthenticatedAccounts] = useState<AuthenticatedAccountDto[]>([])
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)
  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false)

  const visibleTabs = useMemo(
    () => (isPermissionsLoading ? [] : VIEW_TABS.filter(tab => hasPermission(tab.permission))),
    [hasPermission, isPermissionsLoading],
  )

  const requestedView = (searchParams.get('view') as UsersView | null) ?? 'users'
  const activeView = visibleTabs.some(tab => tab.key === requestedView)
    ? requestedView
    : (visibleTabs[0]?.key ?? 'users')

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const isUsersListView = USERS_LIST_VIEWS.includes(activeView)
  const currentSortBy = searchParams.get('sortBy') || (isUsersListView ? 'lastActiveAt' : 'lastName')
  const currentSortOrder = searchParams.get('sortOrder') || 'desc'
  const selectedStatuses = parseStatusesFromParams(searchParams)
  const statusFilter = statusesToParam(selectedStatuses)

  useEffect(() => {
    if (requestedView !== activeView && visibleTabs.length > 0) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.set('view', activeView)
      nextParams.set('page', '1')
      setSearchParams(nextParams, { replace: true })
    }
  }, [activeView, requestedView, searchParams, setSearchParams, visibleTabs.length])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    if (!isUsersListView) {
      return
    }

    const fetchUsers = async () => {
      try {
        const response = await adminService.getUsers(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder,
          statusFilter,
          activeView === 'internal' ? true : undefined,
        )
        const data = response.users.map(user => ({
          ...user,
          inactiveDays: computeInactiveDays(user.lastActiveAt as any, user.createdAt as any),
        }))
        setUsers(data)
        setPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch users', err)
      }
    }

    fetchUsers()
  }, [
    activeView,
    isUsersListView,
    currentPage,
    currentPageSize,
    debouncedSearchValue,
    currentSortBy,
    currentSortOrder,
    statusFilter,
    searchParams,
  ])

  useEffect(() => {
    if (activeView !== 'admins') {
      return
    }

    const fetchAdmins = async () => {
      try {
        const response = await adminService.getAdmins()
        setAdmins(response)
      } catch (err) {
        console.log('Failed to fetch admin accounts', err)
      }
    }

    fetchAdmins()
  }, [activeView])

  useEffect(() => {
    if (activeView !== 'checked-in') {
      return
    }

    const fetchCheckedInUsers = async () => {
      try {
        const response = await adminService.getCheckedInUsers()
        setCheckedInUsers(response)
      } catch (err) {
        console.log('Failed to fetch checked-in users', err)
      }
    }

    fetchCheckedInUsers()
  }, [activeView])

  useEffect(() => {
    if (activeView !== 'logins') {
      return
    }

    const fetchAuthenticatedAccounts = async () => {
      try {
        const response = await adminService.getAuthenticatedAccounts()
        setAuthenticatedAccounts(response)
      } catch (err) {
        console.error('Failed to fetch authenticated accounts', err)
      }
    }

    fetchAuthenticatedAccounts()
  }, [activeView])

  const updateSearchParams = (mutate: (params: URLSearchParams) => void) => {
    const nextParams = new URLSearchParams(searchParams)
    mutate(nextParams)
    setSearchParams(nextParams)
  }

  const handleViewChange = (view: UsersView) => {
    updateSearchParams(params => {
      params.set('view', view)
      params.set('page', '1')
      if (USERS_LIST_VIEWS.includes(view) && !params.get('status')) {
        params.set('status', AccountStatus.Active)
      }
    })
  }

  const handleStatusFilterChange = (statuses: AccountStatus[]) => {
    updateSearchParams(params => {
      params.set('status', statusesToParam(statuses))
      params.set('page', '1')
    })
  }

  const handleUserRowClick = (row: { publicId: string }) => {
    navigate(`/admin/users/${row.publicId}`)
  }

  const handleAdminRowClick = (row: { publicId: string }) => {
    navigate(`/admin/users/${row.publicId}`)
  }

  const handleAdminSaved = (admin: AdminDto) => {
    setAdmins(prev => [...prev, admin])
  }

  const handleAuthenticatedAccountRowClick = (row: AuthenticatedAccountDto) => {
    if (row.role === AccountRole.User && row.userPublicId) {
      navigate(`/admin/users/${row.userPublicId}`)
      return
    }

    navigate(`/admin/users/${row.publicId}`)
  }

  const handlePageChange = (page: number) => {
    updateSearchParams(params => {
      params.set('page', page.toString())
    })
  }

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    updateSearchParams(params => {
      params.set('sortBy', sortBy)
      params.set('sortOrder', sortOrder)
      params.set('page', '1')
    })
  }

  const pageTitle = VIEW_TABS.find(tab => tab.key === activeView)?.label ?? 'Users'

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>{pageTitle}</h2>
        {isUsersListView && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
        {activeView === 'admins' && (
          <button
            type="button"
            onClick={() => setIsCreateAdminModalOpen(true)}
            className="styled-button sm"
          >
            <span>Add Admin</span>
          </button>
        )}
      </div>

      <div className="tab-bar" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {visibleTabs.map(tab => {
            const classNames = ['tab']
            if (tab.key === activeView) {
              classNames.push('active')
            }

            return (
              <button
                key={tab.key}
                type="button"
                className={classNames.join(' ')}
                onClick={() => handleViewChange(tab.key)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {isUsersListView && (
          <AccountStatusFilter selectedStatuses={selectedStatuses} onChange={handleStatusFilterChange} />
        )}
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          {isUsersListView && (
            <Table
              id={activeView === 'internal' ? 'internal-users' : 'users'}
              name={activeView === 'internal' ? 'Internal users' : 'Users'}
              keyField="publicId"
              initialSortColumn={currentSortBy}
              initialSortOrder={currentSortOrder}
              columns={[
                { label: '', accessor: 'imageUrl', type: 'userAvatar' },
                { label: 'Name', accessor: 'lastName', type: 'fullNameAndTelephone' },
                { label: 'Status', accessor: 'status', type: 'enum', enumName: 'AccountStatus' },
                { label: 'Gender', accessor: 'gender', type: 'enum', enumName: 'Gender' },
                {
                  label: 'Preference',
                  accessor: 'preferredGenders',
                  type: 'genderArray',
                  enumName: 'Gender',
                },
                { label: 'Last active', accessor: 'lastActiveAt', type: 'datetime' },
                { label: 'Inactive (days)', accessor: 'inactiveDays', sortable: false },
                { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              ]}
              sourceData={users}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleUserRowClick}
              onSortChange={handleSortChange}
              disableGlobalSearch={true}
              disableClientSort={true}
            />
          )}

          {activeView === 'admins' && (
            <Table
              id="admins"
              name="Administrators"
              keyField="publicId"
              initialSortColumn="lastName"
              columns={[
                { label: '', accessor: 'imageUrl', type: 'avatar' },
                { label: 'Name/Email', accessor: 'lastName', type: 'fullNameAndEmail' },
                { label: 'Status', accessor: 'status', type: 'enum', enumName: 'AccountStatus' },
                { label: 'Last active', accessor: 'lastActiveAt', type: 'datetime' },
                { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              ]}
              sourceData={admins}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleAdminRowClick}
            />
          )}

          {activeView === 'checked-in' && (
            <Table
              id="current-users"
              name="Checked-in users"
              keyField="publicId"
              initialSortColumn="currentVenueCheckedInAt"
              initialSortOrder="desc"
              columns={[
                { label: '', accessor: 'imageUrl', type: 'userAvatar' },
                { label: 'User', accessor: 'firstName', type: 'fullNameAndTelephone' },
                { label: 'Gender', accessor: 'gender', type: 'enum', enumName: 'Gender' },
                {
                  label: 'Preference',
                  accessor: 'preferredGenders',
                  type: 'genderArray',
                  enumName: 'Gender',
                },
                { label: '', accessor: 'currentVenue.logoUrl', type: 'venueAvatar' },
                {
                  label: 'Venue name',
                  accessor: 'currentVenue.name',
                  type: 'nameAndAddress',
                  className: 'width-30p',
                },
                { label: 'Check-in', accessor: 'currentVenueCheckedInAt', type: 'datetime' },
                {
                  label: 'Visit duration',
                  accessor: 'currentVenueCheckedInAt',
                  type: 'intervalFromNow',
                },
              ]}
              sourceData={checkedInUsers}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleUserRowClick}
            />
          )}

          {activeView === 'logins' && (
            <Table
              id="authenticated-accounts"
              name="Current logins"
              keyField="publicId"
              initialSortColumn="lastName"
              columns={[
                { label: '', accessor: 'imageUrl', type: 'avatar' },
                { label: 'Name/Email', accessor: 'lastName', type: 'fullNameAndEmail' },
                { label: 'Kind', accessor: 'role', type: 'enum', enumName: 'AccountRole' },
                { label: 'Status', accessor: 'status', type: 'enum', enumName: 'AccountStatus' },
                { label: 'Last active', accessor: 'lastActiveAt', type: 'datetime' },
                { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              ]}
              sourceData={authenticatedAccounts}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleAuthenticatedAccountRowClick}
            />
          )}
        </div>
      </div>

      <AdminModal
        isOpen={isCreateAdminModalOpen}
        onClose={() => setIsCreateAdminModalOpen(false)}
        onSaved={handleAdminSaved}
      />
    </div>
  )
}

export default UsersPage
